// React
import React from 'react';

// React-Redux
import { useSelector } from 'react-redux';

import { MoreVertical } from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// FutureLab
import { RootState } from '../../reducers/index';
import { returnOneToOneChatUserDetails } from '../../utilities/UserDetails';
import UserDetails from '../popup/userDetails';
import GroupDetails from '../popup/groupDetails';
import { isChatApproved } from '../../utilities/Chat';
import { ModalConfirmation } from '../popup/modalConfirmation';
import { useDeleteAllMessagesMutation, useLeaveChatMutation } from '../../store/api/chatApi';
import { chatItemInFocus } from '../../actions/chatItemInFocusActions';
import { store } from '../../ReduxRoot';
import { removeAllChatRoomPinnedMessages } from '../../actions/pinnedMessagesActions';
import avatarPlaceholder from '../../assets/image/avatar_placeholder.png'
import { isSameUserId, toUserId } from '../../utilities/UserId';

const optionsPersonalChat = [
	{ option: 'Delete all chats', action: 'deleteAllChat' },
	// { option: 'Report this user', action: 'reportUser' },
];

const optionsGroupChat = [
	{ option: 'Delete all chats', action: 'deleteAllChat' },
	// { option: 'Report group', action: 'reportGroup' },
	{ option: 'Leave group', action: 'leaveGroup' },
];

export interface OptionStoreItem {
	option: string;
	action: string;
}

export function DesktopChatRoomHeader() {
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const loggedInUserDetails = useSelector((state: RootState) => state.loggedInUserDetails);
	const userListProfilesDetails = useSelector((state: RootState) => state.userListProfilesDetails);
	const [displayUserDetails, setDisplayUserDetails] = React.useState<boolean>(false);
	const [displayGroupDetails, setDisplayGroupDetails] = React.useState<boolean>(false);
	const [displayModalConfirmation, setDisplayModalConfirmation] = React.useState<boolean>(false);
	const [optionStore, setOptionStore] = React.useState<OptionStoreItem>({ option: '', action: '' });
	const [leaveChat] = useLeaveChatMutation();
	const [deleteAllMessages] = useDeleteAllMessagesMutation();
	const photoUrl = `${process.env.REACT_APP_PROFILE_PHOTOS_BASE_URL}${process.env.REACT_APP_PROFILE_PHOTOS_S3_BASE_URL}${process.env.REACT_APP_PROFILE_PHOTOS_S3_DIRECTORY_URL}`;

	const userProfileDetails = returnOneToOneChatUserDetails(chatInFocus, loggedInUserDetails, userListProfilesDetails);

	const userProfileDetailsFullName = `${userProfileDetails?.firstname} ${userProfileDetails?.lastname ?? ''}`;

	const getGroupChatNameFromParticipantNameInitials = (): string => {
		const groupParticipantIds = chatInFocus.participants.map((participant) => participant.userId);
		const userListProfilesFullDetails = userListProfilesDetails.filter((userListProfileDetails) =>
			groupParticipantIds.includes(userListProfileDetails.id)
		);
		return userListProfilesFullDetails[0].firstname?.charAt(0);
	};

	const getGroupChatNameInitials = (): string => {
		const nameFromDb = chatInFocus?.name?.trim()?.charAt(0);
		return nameFromDb ? nameFromDb : getGroupChatNameFromParticipantNameInitials();
	};

	const getGroupChatNameFromParticipantNames = (): string => {
		const groupParticipantIds = chatInFocus.participants.map((participant) => participant.userId);
		const userListProfilesFullDetails = userListProfilesDetails.filter((userListProfileDetails) =>
			groupParticipantIds.includes(userListProfileDetails.id)
		);
		let finalGroupName = '';
		userListProfilesFullDetails.map(
			(userListProfileFullDetails, index) =>
				(finalGroupName += `${index > 0 ? ',' : ''} ${userListProfileFullDetails.firstname}`)
		);
		return finalGroupName;
	};

	const getGroupChatName = (): string => {
		const nameFromDb = chatInFocus.name;
		return nameFromDb ? nameFromDb : getGroupChatNameFromParticipantNames();
	};

	const checkGroupOption = chatInFocus?.chatAdmins?.some(
		(participant) => isSameUserId(participant.userId, toUserId(loggedInUserDetails.id))
	)
		? optionsGroupChat
		: [optionsGroupChat[1]];

	const optionSelectorChatMessages = (option: string) => {
		if (option === optionsPersonalChat[0].option || option === optionsGroupChat[0].option) {
			deleteAllChatCallBack();
		}
		if (option === optionsGroupChat[1].option) {
			leaveGroupCallBack();
		}
	};

	const leaveGroupCallBack = () => {
		const currentUserId = toUserId(loggedInUserDetails.id);
		const currentUserParticipantId = chatInFocus.participants
			.filter((participant) => isSameUserId(participant.userId, currentUserId))
			.find((ele) => ele)?._id;
		leaveChat({
			chatId: chatInFocus?.id.toLocaleString(),
			participantId: currentUserParticipantId?.toLocaleString(),
		})
			.unwrap()
			.then(() => {
			store.dispatch(chatItemInFocus(null));
			setDisplayModalConfirmation(false);
		});
	};

	const deleteAllChatCallBack = () => {
		deleteAllMessages({
			chatId: chatInFocus?.id.toLocaleString(),
		})
			.unwrap()
			.then((response) => {
			store.dispatch(removeAllChatRoomPinnedMessages(response.pinnedMessagesDeleted));
			setDisplayModalConfirmation(false);
		});
	};

	const openPopUp = (option: string) => {
		setDisplayModalConfirmation(!displayModalConfirmation);
	};

	const renderGroupOption = () => {
		return checkGroupOption.map((optionGroupChat) => (
			<DropdownMenuItem
				key={optionGroupChat.option}
				className="min-h-10"
				onClick={() => {
					setOptionStore(optionGroupChat);
					openPopUp(optionGroupChat.option);
				}}
			>
					{optionGroupChat.option}
			</DropdownMenuItem>
		));
	};

	const renderPersonalOption = () => {
		return optionsPersonalChat.map((optionPersonalChat) => (
			<DropdownMenuItem
				key={optionPersonalChat.option}
				className="min-h-10"
				onClick={() => {
					setOptionStore(optionPersonalChat);
					openPopUp(optionPersonalChat.option);
				}}
			>
					{optionPersonalChat.option}
			</DropdownMenuItem>
		));
	};

	return (
		<div className="shrink-0 bg-white">
			{chatInFocus.isGroupChat ? (
				<div className="border-b border-solid px-4 py-2">
					<div className="flex items-center">
						<div className="flex min-w-0 flex-1 items-center">
							<Avatar
								className="size-11 cursor-pointer"
									onClick={() => {
										setDisplayGroupDetails(!displayGroupDetails);
									}}
								>
									<AvatarFallback>{getGroupChatNameInitials().toLocaleUpperCase()}</AvatarFallback>
								</Avatar>
							{displayGroupDetails && (
								<GroupDetails
									setDisplayGroupDetails={setDisplayGroupDetails}
									displayGroupDetails={displayGroupDetails}
									getGroupChatNameInitials={getGroupChatNameInitials}
									getGroupChatName={getGroupChatName}
								/>
							)}
							<div className="min-w-0 truncate pl-3 text-base capitalize text-gray-900">
									{getGroupChatName()}
							</div>
						</div>
						<div className="ml-2">
							{isChatApproved(chatInFocus, loggedInUserDetails?.id) ? (
								<DropdownMenu>
									<DropdownMenuTrigger aria-label="Chat options" className="inline-flex size-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
										<MoreVertical className="size-5" />
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" className="w-40 text-center">
										{renderGroupOption()}
									</DropdownMenuContent>
								</DropdownMenu>
							) : (
								''
							)}
						</div>
					</div>
					{displayModalConfirmation && (
						<ModalConfirmation
							displayModalConfirmation={displayModalConfirmation}
							setDisplayModalConfirmation={setDisplayModalConfirmation}
							optionStore={optionStore}
							callBackProceedMethodProp={() => optionSelectorChatMessages(optionStore?.option)}
						/>
					)}
				</div>
			) : (
				<div className="border-b border-solid px-4 py-2">
					<div className="flex items-center">
						<div className="flex min-w-0 flex-1 items-center">
								{!!userProfileDetails?.profile_photo ? (
									<button
										type="button"
										aria-label={`View ${userProfileDetailsFullName}`}
										onClick={() => {
											setDisplayUserDetails(!displayUserDetails);
										}}
									>
									<img
										alt=""
										src={`${photoUrl}/${userProfileDetails?.id}/${userProfileDetails?.profile_photo}`}
										className="chat-list-image-holder object-cover"
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = avatarPlaceholder;
                      }}
										/>
									</button>
								) : (
									<Avatar
									className="size-11 cursor-pointer"
										onClick={() => {
											setDisplayUserDetails(!displayUserDetails);
										}}
									>
										<AvatarFallback>
											{userProfileDetails?.firstname?.charAt(0).toLocaleUpperCase()}
											{userProfileDetails?.lastname?.charAt(0).toLocaleUpperCase() ?? ''}
										</AvatarFallback>
									</Avatar>
								)}
							{displayUserDetails && (
								<UserDetails
									setDisplayUserDetails={setDisplayUserDetails}
									displayUserDetails={displayUserDetails}
									userProfileDetails={userProfileDetails}
									photoUrl={photoUrl}
								/>
							)}
							<div className="min-w-0 truncate pl-3 text-base capitalize text-gray-900">
									{userProfileDetailsFullName}
							</div>
						</div>
						<div className="ml-2">
							<DropdownMenu>
								<DropdownMenuTrigger aria-label="Chat options" className="inline-flex size-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
									<MoreVertical className="size-5" />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" className="w-40 text-center">
									{renderPersonalOption()}
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					</div>
					{displayModalConfirmation && (
						<ModalConfirmation
							displayModalConfirmation={displayModalConfirmation}
							setDisplayModalConfirmation={setDisplayModalConfirmation}
							optionStore={optionStore}
							callBackProceedMethodProp={() => optionSelectorChatMessages(optionStore?.option)}
						/>
					)}
				</div>
			)}
		</div>
	);
}
